import { useEffect, useMemo, useState } from 'react';
import { LIST_PAGES, emptyNote, t, type DocumentNotesKind, type NoteDraft, type PagedRecord } from '@bms2/shared';
import { NoteModal, type NamedRow } from '../../components/modals/NoteModal';
import { PAGES_PATH, apiFetch, patch, post } from './api';
import { linkedSearch } from './CalendarPage';
import { reportCaughtInBrowser } from '../../shell/systemErrors';

/**
 * The banner's + Log Contact and a note's pencil (piece 13, decisions 0185
 * and 0910): the one Add/Edit Note modal every record uses, with its
 * lists read from the API and its save landing on this record. A note logged
 * as a Call or a Meeting is what moves the contact clock. Handed a `noteId`,
 * the same modal reopens on that note and saves through PATCH: the row's
 * pencil is drawn for the author alone (decision 0301).
 */
export interface RecordNoteModalProps {
  open: boolean;
  /** 🔒 A record with a page, or a document kind the notes panel reaches that has no record route of its own, the Change Order (decision 0951). */
  recordType: PagedRecord | DocumentNotesKind;
  recordId: string;
  /** The record's own name, for the fixed chip; the kind is read from the type. */
  recordLabel?: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
  onError?: (message: string) => void;
  openerId?: string | null;
  /** The note being edited and what it holds; absent when adding one. */
  noteId?: string | null;
  initial?: NoteDraft | undefined;
}

interface NoteOptions {
  issueFlags: NamedRow[];
  activityKinds: NamedRow[];
  confidentialityRoles: NamedRow[];
}

const NO_OPTIONS: NoteOptions = { issueFlags: [], activityKinds: [], confidentialityRoles: [] };

/** What a note's write carries, from the modal's draft; the one shape the notes route is sent. */
export function noteWriteBody(draft: NoteDraft) {
  return {
    text: draft.body, activityKindId: draft.activityKindId, direction: draft.direction, durationMinutes: draft.durationMinutes, issueFlagId: draft.issueFlagId,
    links: draft.links.map((link) => ({ recordType: link.recordType, recordId: link.recordId })),
  };
}

/**
 * 🔒 A notes list's composer writes through the route the Add Note modal wrote
 * by (decision 0910), with every default the modal opens on: no activity kind,
 * no issue flag, no further record. The author's pencil opens the full form.
 * Answers the server's refusal in words, or null once the note is saved.
 */
export async function addNoteText(recordType: string, recordId: string, text: string, apiBaseUrl = ''): Promise<string | null> {
  const answer = await post(`${PAGES_PATH}/notes/${recordType}/${recordId}`, noteWriteBody({ ...emptyNote(), body: text }), apiBaseUrl);
  return answer.ok ? null : answer.message;
}

export function RecordNoteModal({ open, recordType, recordId, recordLabel, apiBaseUrl = '', onCancel, onSaved, onError, openerId = null, noteId = null, initial }: RecordNoteModalProps) {
  const [options, setOptions] = useState<NoteOptions>(NO_OPTIONS);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    apiFetch(`${apiBaseUrl}${PAGES_PATH}/note-options`, { signal: controller.signal })
      .then((response) => (response.ok ? (response.json() as Promise<Partial<NoteOptions>>) : NO_OPTIONS))
      .then((body) => {
        // 🔨 The modal survives an answer missing a list; a fixture without one crashed the page (2026-09-11).
        if (!controller.signal.aborted) setOptions({ issueFlags: body.issueFlags ?? [], activityKinds: body.activityKinds ?? [], confidentialityRoles: body.confidentialityRoles ?? [] });
      })
      .catch((failed: unknown) => {
        reportCaughtInBrowser('records.noteOptions.read', failed);
      });
    return () => controller.abort();
  }, [open, apiBaseUrl]);

  /** Also about searches every record kind the global search lists, the way an event's Linked To does (decisions 0275, 0339). */
  const searchRecords = useMemo(() => linkedSearch(apiBaseUrl), [apiBaseUrl]);
  // The fixed chip: an edit opened from a linked record names the note's own origin; otherwise it is this page's record.
  const originLabel = initial?.taggedRecordId && initial.taggedRecordId !== recordId && typeof initial.links !== 'undefined' && (initial as NoteDraft & { originLabel?: string }).originLabel
    ? (initial as NoteDraft & { originLabel?: string }).originLabel!
    : `${recordType in LIST_PAGES ? LIST_PAGES[recordType as PagedRecord].singular : t('documentName.changeOrder')} · ${recordLabel ?? recordId}`;

  const save = async (draft: NoteDraft) => {
    const body = noteWriteBody(draft);
    const answer = noteId ? await patch(`${PAGES_PATH}/notes/${noteId}`, body, apiBaseUrl) : await post(`${PAGES_PATH}/notes/${recordType}/${recordId}`, body, apiBaseUrl);
    if (!answer.ok) {
      onError?.(answer.message);
      return;
    }
    onSaved();
  };

  return (
    <NoteModal
      open={open}
      issueFlags={options.issueFlags}
      activityKinds={options.activityKinds}
      confidentialityRoles={options.confidentialityRoles}
      searchRecords={searchRecords}
      originLabel={originLabel}
      {...(initial ? { initial } : {})}
      {...(noteId ? { title: t('note.editTitle') } : {})}
      recordType={recordType}
      recordId={recordId}
      onSave={(draft) => void save(draft)}
      onCancel={onCancel}
      openerId={openerId}
    />
  );
}
